import { ReactElement, ReactNode, useEffect, useState, Suspense, memo, useMemo } from 'react';

import { useNavigate } from 'react-router-dom';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { useApp } from '../../contextProviders/AppProvider';
import { useData } from '../../contextProviders/DataProvider';
import Person from '../../types/Person';

import GratiGodActions from './GratiGodActions';
import GratiSystemError from './GratiSystemError';
import Hourglass from './Hourglass';

interface GratiPageWrapperProps {
  children: ReactNode;
  isContentLoading?: boolean;
  isAuthRequired?: boolean;
  isNoAuthRequired?: boolean;
  validateUser?: (userProfile: Person | null | undefined) => boolean;
  godActions?: ReactElement | null;
  error?: Error | null;
  contentError?: Error | null; // System-level errors that occurred during page data loading
  onErrorRetry?: () => void; // Optional callback for custom retry behavior
}

const MemoizedGratiGodActions = memo(GratiGodActions);

export default function GratiPageWrapper(props: GratiPageWrapperProps) {
  const { children, validateUser, godActions, error, contentError, onErrorRetry } = props;
  const isNoAuthRequired = props.isNoAuthRequired ?? false;
  const isAuthRequired = props.isAuthRequired ?? !isNoAuthRequired;
  const isContentLoading = props.isContentLoading ?? false;

  const navigate = useNavigate();
  const { updateActions } = useApp();
  const { userState } = useData();

  const memoizedUserProfile = useMemo(() => {
    return userState.status === 'ready' ? userState.userProfile : null;
  }, [userState.status, userState.userProfile]);

  const [isWrapperLoading, setIsWrapperLoading] = useState(true);
  const [isPageReady, setIsPageReady] = useState(false);

  useEffect(() => {
    return () => {
      updateActions([]);
    };
  }, [updateActions]);

  useEffect(() => {
    setIsWrapperLoading(true);
  }, []);

  useEffect(() => {
    if (userState.status === 'ready' || userState.status === 'logged_out') {
      if (memoizedUserProfile) {
        if (validateUser && !validateUser(memoizedUserProfile)) {
          navigate('/');
        }
        if (isNoAuthRequired) {
          navigate('/');
        }
        setIsWrapperLoading(false);
      } else {
        if (isAuthRequired) {
          navigate('/login');
        }
        setIsWrapperLoading(false);
      }
    }
  }, [memoizedUserProfile, userState.status, validateUser, navigate, isAuthRequired, isNoAuthRequired]);

  useEffect(() => {
    if (!isWrapperLoading && !isContentLoading) {
      setIsPageReady(true);
    }
  }, [isWrapperLoading, isContentLoading]);

  if (error) {
    return (
      <Box>
        <Typography component="h6" color="error">
          {error.message}
        </Typography>
      </Box>
    );
  }

  if (contentError) {
    return <GratiSystemError error={contentError} onRetry={onErrorRetry} />;
  }

  if (!isPageReady) {
    return <Hourglass />;
  } else {
    return (
      <Suspense fallback={<Hourglass />}>
        <>
          {children}
          <MemoizedGratiGodActions pageGodActions={godActions} />
        </>
      </Suspense>
    );
  }
}
